import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Building2,
  Check,
  Fingerprint,
  Loader2,
  LogOut,
  Receipt,
  ShieldCheck,
  Trash2,
  UserRound,
} from 'lucide-react';
import { apiFetch } from '../lib/api';
import { useAuthStore } from '../store/authStore';
import { useSurfaceTheme } from '../hooks/useSurfaceTheme';
import { MiPlan } from '../components/MiPlan';
import { sesionApi } from '../lib/sesion';

/**
 * MI CUENTA (/cuenta) — el menú de la persona: perfil, plan y consumo,
 * sesión, y la zona de peligro (eliminar cuenta, autoservicio).
 * Borrar la cuenta NO borra los aportes: el acta es INSERT-only (C1); la
 * evidencia queda anónima (user_id NULL) pero íntegra.
 */
/** El rol dicho en una línea, no en un manual. */
const ROLES_HUMANOS: Record<string, { etiqueta: string; explica: string }> = {
  miembro: {
    etiqueta: 'Participante',
    explica: 'Aportás en los procesos a los que te inviten. No componés ni conducís.',
  },
  facilitador: {
    etiqueta: 'Facilitador',
    explica:
      'Componés y conducís tus procesos. Ves los que facilitás, no todos los de tu organización.',
  },
  admin_org: {
    etiqueta: 'Administra la organización',
    explica:
      'Ves todos los procesos de tu organización y administrás su gente, sus asientos y su identidad.',
  },
  superadmin: {
    etiqueta: 'Superadmin de plataforma',
    explica: 'Cruzás organizaciones y administrás planes y tenants. No tenés topes.',
  },
};

function iniciales(nombre: string): string {
  return nombre
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
}

function Tarjeta({
  titulo,
  icono: Icono,
  children,
  peligro = false,
}: {
  titulo: string;
  icono: typeof UserRound;
  children: React.ReactNode;
  peligro?: boolean;
}) {
  return (
    <section
      className={
        peligro
          ? 'rounded-xl border border-error/40 bg-surface-raised p-4 sm:p-5'
          : 'rounded-xl border border-border bg-surface-raised p-4 sm:p-5'
      }
    >
      <h2
        className={
          peligro
            ? 'flex items-center gap-2 font-display text-sm font-semibold text-error'
            : 'flex items-center gap-2 font-display text-sm font-semibold text-content'
        }
      >
        <Icono className="h-4 w-4" aria-hidden="true" />
        {titulo}
      </h2>
      {children}
    </section>
  );
}

export function CuentaPage() {
  useSurfaceTheme('bolsillo', 'light');
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const hydrate = useAuthStore((s) => s.hydrate);

  const rol = ROLES_HUMANOS[user?.rol ?? 'miembro'] ?? ROLES_HUMANOS.miembro;
  const administra = user?.rol === 'admin_org' || user?.rol === 'superadmin';

  const participaciones = useQuery({
    queryKey: ['mis-participaciones'],
    queryFn: sesionApi.misParticipaciones,
  });
  const totalAportes = useMemo(
    () =>
      (participaciones.data ?? []).reduce(
        (suma, p) => suma + (p.sesiones ?? []).reduce((s, x) => s + (x.mis_aportes ?? 0), 0),
        0,
      ),
    [participaciones.data],
  );

  const [nombre, setNombre] = useState(user?.nombre ?? '');
  const [password, setPassword] = useState('');
  const [confirmacion, setConfirmacion] = useState('');

  const pagos = useQuery({
    queryKey: ['pagos', 'mios'],
    queryFn: () =>
      apiFetch<{ id: string; plan_codigo: string; monto_centavos: number; moneda: string; estado: string; created_at: string }[]>(
        '/pagos/mios',
      ),
  });

  const guardar = useMutation({
    mutationFn: () =>
      apiFetch('/auth/me', {
        method: 'PATCH',
        json: { nombre: nombre.trim(), password: password || null },
      }),
    onSuccess: () => {
      setPassword('');
      void hydrate();
    },
  });

  const eliminar = useMutation({
    mutationFn: () =>
      apiFetch('/auth/me', { method: 'DELETE', json: { confirmar: confirmacion } }),
    onSuccess: async () => {
      await logout().catch(() => undefined);
      navigate('/', { replace: true });
    },
  });

  async function onLogout() {
    await logout();
    navigate('/login', { replace: true });
  }

  return (
    <div className="min-h-screen bg-surface-base pb-16">
      {/* Cabecera con identidad: quién soy, de dónde y con qué rol. */}
      <header className="border-b border-border bg-surface-raised">
        <div className="mx-auto max-w-3xl px-4 pt-4 sm:px-6">
          <Link
            to="/entrar"
            className="inline-flex min-h-touch items-center gap-1.5 font-sans text-sm text-content-muted hover:text-content"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            Volver
          </Link>
        </div>
        <div className="mx-auto flex max-w-3xl flex-wrap items-center gap-4 px-4 pb-5 pt-3 sm:px-6">
          <span
            aria-hidden="true"
            className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-brand-gradient font-display text-xl font-bold text-white shadow-md"
          >
            {iniciales(user?.nombre ?? '?')}
          </span>
          <div className="min-w-0 flex-1">
            <h1 className="truncate font-display text-xl font-bold text-content">
              {user?.nombre ?? 'Mi cuenta'}
            </h1>
            <p className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 font-sans text-xs text-content-muted">
              {user?.correo && <span className="truncate">{user.correo}</span>}
              {user?.org_nombre && (
                <>
                  <span aria-hidden="true">·</span>
                  <span className="inline-flex items-center gap-1">
                    <Building2 className="h-3.5 w-3.5" aria-hidden="true" />
                    {user.org_nombre}
                  </span>
                </>
              )}
            </p>
          </div>
          <span className="rounded-full bg-surface-sunken px-3 py-1 font-sans text-xs font-semibold text-content">
            {rol.etiqueta}
          </span>
        </div>
      </header>

      <main className="mx-auto flex max-w-3xl flex-col gap-4 px-4 py-5 sm:px-6">
        {/* Qué puedo hacer con este rol, en una línea. */}
        <p className="rounded-xl border border-dashed border-border px-4 py-3 font-sans text-sm text-content-muted">
          {rol.explica}
        </p>

        {/* Lo primero que alguien viene a mirar: su plan y cuánto lleva usado. */}
        <MiPlan />

        {/* ── Perfil y acceso ── */}
        <Tarjeta titulo="Perfil y acceso" icono={Fingerprint}>
          <form
            className="mt-3 flex flex-wrap items-end gap-3"
            onSubmit={(e) => {
              e.preventDefault();
              if (nombre.trim().length >= 2) guardar.mutate();
            }}
          >
            <label className="flex min-w-[220px] flex-1 flex-col gap-1 font-sans text-xs text-content-muted">
              Tu nombre (así aparecés en las actas)
              <input
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                className="rounded-lg border border-border bg-surface-base px-3 py-2 font-sans text-sm text-content"
              />
            </label>
            <label className="flex min-w-[200px] flex-1 flex-col gap-1 font-sans text-xs text-content-muted">
              Contraseña nueva (opcional; además de Google)
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="mínimo 8 caracteres"
                className="rounded-lg border border-border bg-surface-base px-3 py-2 font-sans text-sm text-content"
              />
            </label>
            <button
              type="submit"
              disabled={nombre.trim().length < 2 || (password !== '' && password.length < 8) || guardar.isPending}
              className="inline-flex min-h-[40px] items-center gap-1.5 rounded-lg bg-primary px-4 py-2 font-sans text-sm font-semibold text-white transition hover:bg-primary/90 disabled:opacity-40"
            >
              {guardar.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              ) : guardar.isSuccess ? (
                <Check className="h-4 w-4" aria-hidden="true" />
              ) : null}
              Guardar
            </button>
          </form>
          <p className="mt-2 font-sans text-xs text-content-muted">
            Entrás con Google. Poner contraseña te da una segunda vía, no reemplaza
            la primera.
          </p>
        </Tarjeta>

        {/* ── Dónde participo ── */}
        <Tarjeta titulo="Dónde participo" icono={UserRound}>
          {participaciones.isLoading ? (
            <p className="mt-2 font-sans text-sm text-content-muted">Cargando…</p>
          ) : (participaciones.data ?? []).length === 0 ? (
            <p className="mt-2 font-sans text-sm text-content-muted">
              Todavía no participás en ningún proceso. Cuando te inviten a una sala,
              aparece acá junto con lo que aportaste.
            </p>
          ) : (
            <>
              <p className="mt-1 font-sans text-xs text-content-muted">
                {(participaciones.data ?? []).length} proceso
                {(participaciones.data ?? []).length === 1 ? '' : 's'} · {totalAportes} aporte
                {totalAportes === 1 ? '' : 's'} tuyos en total
              </p>
              <ul className="mt-3 divide-y divide-border">
                {(participaciones.data ?? []).map((p) => {
                  const mios = (p.sesiones ?? []).reduce((s, x) => s + (x.mis_aportes ?? 0), 0);
                  return (
                    <li key={p.proceso_id} className="flex flex-wrap items-center gap-2 py-2.5">
                      <span className="min-w-0 flex-1">
                        <span className="block truncate font-sans text-sm font-medium text-content">
                          {p.proceso_nombre}
                        </span>
                        <span className="font-sans text-xs text-content-muted">
                          {p.org_nombre} · como {p.rol}
                          {p.grupo_nombre ? ` · ${p.grupo_nombre}` : ''}
                        </span>
                      </span>
                      <span className="rounded-full bg-surface-sunken px-2.5 py-0.5 font-sans text-[11px] font-semibold text-content-muted">
                        {mios} aporte{mios === 1 ? '' : 's'}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </Tarjeta>

        {/* ── Organización (solo quien la administra) ── */}
        {administra && (
          <Tarjeta titulo="Mi organización" icono={Building2}>
            <p className="mt-2 font-sans text-sm text-content-muted">
              Administrás la gente, los asientos y la identidad de{' '}
              <strong className="text-content">{user?.org_nombre}</strong>: su logo, su
              nombre y la dirección con la que entra tu equipo.
            </p>
            <Link
              to="/organizacion"
              className="mt-3 inline-flex min-h-touch items-center gap-1.5 rounded-lg border border-border-strong px-4 py-2 font-sans text-sm font-semibold text-content transition hover:bg-surface-sunken"
            >
              <ShieldCheck className="h-4 w-4" aria-hidden="true" />
              Ir a mi organización
            </Link>
          </Tarjeta>
        )}

        {/* ── Historial de pagos ── */}
        {(pagos.data ?? []).length > 0 && (
          <Tarjeta titulo="Mis pagos" icono={Receipt}>
            <ul className="mt-2 divide-y divide-border">
              {(pagos.data ?? []).map((p) => (
                <li key={p.id} className="flex flex-wrap items-center gap-2 py-2 font-sans text-sm">
                  <span className="font-medium capitalize text-content">{p.plan_codigo}</span>
                  <span className="text-content-muted">
                    ${(p.monto_centavos / 100).toFixed(2)} {p.moneda}
                  </span>
                  <span className="flex-1" />
                  <span className="text-xs text-content-muted">
                    {new Date(p.created_at).toLocaleDateString('es-GT', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </span>
                  <span
                    className={
                      p.estado === 'pagado'
                        ? 'rounded-full bg-success/10 px-2 py-0.5 text-[11px] font-semibold text-success'
                        : p.estado === 'fallido'
                          ? 'rounded-full bg-error/10 px-2 py-0.5 text-[11px] font-semibold text-error'
                          : 'rounded-full bg-surface-sunken px-2 py-0.5 text-[11px] font-semibold text-content-muted'
                    }
                  >
                    {p.estado}
                  </span>
                </li>
              ))}
            </ul>
          </Tarjeta>
        )}

        {/* ── Sesión ── */}
        <Tarjeta titulo="Sesión" icono={LogOut}>
          <button
            type="button"
            onClick={() => void onLogout()}
            className="mt-3 inline-flex min-h-touch items-center gap-1.5 rounded-lg border border-border-strong px-4 py-2 font-sans text-sm font-semibold text-content transition hover:bg-surface-sunken"
          >
            <LogOut className="h-4 w-4" aria-hidden="true" />
            Cerrar sesión
          </button>
        </Tarjeta>

        {/* Zona de peligro: última y separada por una línea. Eliminar la cuenta
            no puede estar al lado de cambiar el nombre. */}
        <div className="mt-4 border-t border-border pt-4">
        <Tarjeta titulo="Eliminar mi cuenta" icono={Trash2} peligro>
          <p className="mt-1 font-sans text-xs text-content-muted">
            Se borra tu cuenta, tus accesos y tus membresías, definitivamente, y
            se intenta cancelar tu suscripción de pago. Tus aportes en actas ya
            congeladas no se borran: quedan anónimos (el acta es un registro
            íntegro). Para confirmar, escribí{' '}
            <strong className="text-content">ELIMINAR</strong>.
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <input
              value={confirmacion}
              onChange={(e) => setConfirmacion(e.target.value)}
              placeholder="ELIMINAR"
              className="rounded-lg border border-border bg-surface-base px-3 py-2 font-sans text-sm text-content"
              aria-label="Escribí ELIMINAR para confirmar"
            />
            <button
              type="button"
              disabled={confirmacion.trim().toUpperCase() !== 'ELIMINAR' || eliminar.isPending}
              onClick={() => eliminar.mutate()}
              className="inline-flex min-h-touch items-center gap-1.5 rounded-lg bg-error px-4 py-2 font-sans text-sm font-bold text-white transition hover:bg-error/90 disabled:opacity-40"
            >
              {eliminar.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              ) : (
                <Trash2 className="h-4 w-4" aria-hidden="true" />
              )}
              Eliminar mi cuenta
            </button>
          </div>
          {eliminar.isError && (
            <p className="mt-2 font-sans text-xs text-error">
              {(eliminar.error as { detail?: string })?.detail ?? 'No se pudo eliminar.'}
            </p>
          )}
        </Tarjeta>
        </div>
      </main>
    </div>
  );
}
